import { createSlice } from '@reduxjs/toolkit';
import { has, isEmpty, sortBy } from 'lodash';

const initialState = {
    isOpenDrawer: false,
    hoveredParentId: null,
    bookings: {},
};

export const bookingsSlice = createSlice({
    name: 'bookings',
    initialState,
    reducers: {
        clearBookings: () => ({
            isOpenDrawer: false,
            hoveredParentId: null,
            bookings: {},
        }),
        openBookingDrawer: (state) => {
            state.isOpenDrawer = true;
        },
        closeBookingDrawer: (state) => {
            state.isOpenDrawer = false;
        },
        setHoveredParentId: (state, action) => {
            state.hoveredParentId = action.payload;
        },
        clearHoveredParentId: (state) => {
            state.hoveredParentId = null;
        },
        deleteBooking: (state, action) => {
            const { categoryId, room, index } = action.payload;
            if (!has(state.bookings, categoryId) || !has(state.bookings[categoryId], room)) return state;

            const newBookings = [];
            state.bookings[categoryId][room].forEach((booking) => {
                // If the clicked date is not in this booking range, keep it as is
                if (!(booking.start <= index && index <= booking.end)) {
                    newBookings.push(booking);
                    return;
                }

                // If booking is only 1 day, delete it completely
                if (booking.start === booking.end) {
                    return;
                }

                // If clicked date is at the start, keep only the right part
                if (index === booking.start) {
                    newBookings.push({
                        ...booking,
                        start: index + 1,
                    });
                    return;
                }

                // If clicked date is at the end, keep only the left part
                if (index === booking.end) {
                    newBookings.push({
                        ...booking,
                        end: index - 1,
                    });
                    return;
                }

                // If clicked date is in the middle, split into two bookings
                // Left part: from start to (index - 1)
                newBookings.push({
                    ...booking,
                    end: index - 1,
                });

                // Right part: from (index + 1) to end
                newBookings.push({
                    ...booking,
                    start: index + 1,
                });
            });

            if (isEmpty(newBookings)) delete state.bookings[categoryId][room];
            if (isEmpty(state.bookings[categoryId])) delete state.bookings[categoryId];
            else state.bookings[categoryId][room] = newBookings;
            return state;
        },
        addBooking: (state, action) => {
            const categoryId = action.payload.categoryId;
            const room = action.payload.room;

            if (!has(state.bookings, categoryId)) state.bookings[categoryId] = {};
            if (!has(state.bookings[categoryId], room)) state.bookings[categoryId][room] = [];
            state.bookings[categoryId][room].push(action.payload);
            state.bookings[categoryId][room] = mergeOverlappingBookings(state.bookings[categoryId][room]);
            return state;
        },
    },
});

const mergeOverlappingBookings = (bookings) => {
    if (!bookings.length) return [];

    let sorted = sortBy(bookings, 'start');
    let merged = [];
    let prev = sorted[0];

    for (let i = 1; i < sorted.length; i++) {
        let current = sorted[i];
        if (current.start <= prev.end + 1) {
            prev.end = Math.max(prev.end, current.end);
            if (prev.endDate && current.endDate) {
                prev.endDate = prev.endDate > current.endDate ? prev.endDate : current.endDate;
            }
        } else {
            merged.push(prev);
            prev = current;
        }
    }

    merged.push(prev);
    return merged;
};

export const { clearBookings, addBooking, deleteBooking, openBookingDrawer, closeBookingDrawer, setHoveredParentId, clearHoveredParentId } =
    bookingsSlice.actions;
export default bookingsSlice.reducer;
